import { useCallback, useEffect, useRef, useState } from 'react'
import ApiService from '@services/api'

const CYCLE_MS = 30000

const getCycleState = () => {
  const elapsed = Date.now() % CYCLE_MS
  return {
    cycle: Math.floor(Date.now() / CYCLE_MS),
    remainingSeconds: Math.ceil((CYCLE_MS - elapsed) / 1000),
    progress: ((CYCLE_MS - elapsed) / CYCLE_MS) * 100,
  }
}

export default function PjeQuickCopyButton({ variant = 'compact' }) {
  const initialCycle = getCycleState()
  const [credencialId, setCredencialId] = useState(null)
  const [codigo, setCodigo] = useState('')
  const [remainingSeconds, setRemainingSeconds] = useState(initialCycle.remainingSeconds)
  const [progress, setProgress] = useState(initialCycle.progress)
  const [status, setStatus] = useState('loading')
  const currentCycle = useRef(initialCycle.cycle)

  const carregarCodigo = useCallback(async () => {
    let id = credencialId

    if (!id) {
      const credencialResponse = await ApiService.getMinhaCredencialPje()
      if (!credencialResponse.success || !credencialResponse.data) {
        setStatus('unavailable')
        setCodigo('')
        return
      }
      id = credencialResponse.data.id
      setCredencialId(id)
    }

    const tokenResponse = await ApiService.getPjeToken(id)
    if (tokenResponse.success) {
      setCodigo(tokenResponse.data.codigo)
      setStatus('ready')
    } else {
      setCodigo('')
      setStatus('error')
    }
  }, [credencialId])

  useEffect(() => {
    carregarCodigo()
  }, [carregarCodigo])

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      const next = getCycleState()
      setRemainingSeconds(next.remainingSeconds)
      setProgress(next.progress)

      if (next.cycle !== currentCycle.current) {
        currentCycle.current = next.cycle
        carregarCodigo()
      }
    }, 250)

    return () => window.clearInterval(intervalId)
  }, [carregarCodigo])

  const copiarCodigo = async () => {
    if (!/^\d{6}$/.test(codigo)) return

    try {
      await navigator.clipboard.writeText(codigo)
      setStatus('copied')
      window.setTimeout(() => setStatus('ready'), 1600)
    } catch {
      setStatus('error')
    }
  }

  const label = {
    loading: 'Carregando 2FA...',
    ready: 'Copiar 2FA PJe',
    copied: 'Código copiado',
    unavailable: '2FA não configurado',
    error: 'Erro ao copiar 2FA',
  }[status]

  if (variant === 'fullscreen') {
    return (
      <section className={`pje-fullscreen-token is-${status}`} aria-live="polite" aria-label="Código 2FA PJe">
        <span className="pje-fullscreen-token__label">
          <i className="fas fa-shield-alt" /> Token PJe
        </span>
        <strong className="pje-fullscreen-token__code">{codigo || label}</strong>
        <span className="pje-fullscreen-token__remaining">Renova em {remainingSeconds}s</span>
        <div className="pje-fullscreen-token__timer" aria-hidden="true">
          <span style={{ width: `${progress}%` }} />
        </div>
      </section>
    )
  }

  return (
    <div className={`pje-quick-copy is-${status}`}>
      <button
        type="button"
        className="pje-quick-copy__button"
        onClick={copiarCodigo}
        disabled={!/^\d{6}$/.test(codigo)}
        aria-label={label}
      >
        <i className={`fas ${status === 'copied' ? 'fa-check' : 'fa-shield-alt'}`} />
        <span>{label}</span>
        {codigo ? <span className="pje-quick-copy__tooltip" role="tooltip">{codigo}</span> : null}
      </button>
      <div className="pje-quick-copy__timer" aria-label={`${remainingSeconds} segundos restantes`}>
        <span style={{ width: `${progress}%` }} />
      </div>
    </div>
  )
}
